/**
 * The connection to a room.
 *
 * A socket to a room is not a one-shot thing: laptops sleep, wifi drops, and
 * the server restarts. So this reconnects on its own, with a backoff, and
 * re-sends the message that got it into the room in the first place — a `join`
 * carrying the seat's session token, or a `spectate`. Everything else follows
 * from that, because the view the server sends back is whole.
 */
import type { ClientMessage, ServerMessage } from '@trajan/protocol';

/**
 * Where the server is. In production the server serves this page, so its own
 * origin is the answer and nothing has to be baked in at build time; in `pnpm
 * dev` the client is on Vite's port and the server on its own. `VITE_SERVER_URL`
 * overrides both, for a deployment that hosts the client somewhere else.
 */
export const SERVER_URL: string =
  import.meta.env.VITE_SERVER_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : window.location.origin);

export type Listener = (msg: ServerMessage) => void;

/** What the person needs to know about the wire, and nothing more. */
export type Link = 'connecting' | 'open' | 'reconnecting';

/** Long enough not to hammer a server that is still booting, short enough to feel quick. */
const BACKOFF_MS = [250, 500, 1000, 2000, 4000, 8000] as const;

export class GameSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(link: Link) => void>();
  /** The message that gets this connection back where it was. */
  private hello: ClientMessage | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;

  connect(): void {
    this.closed = false;
    this.open();
  }

  /**
   * The message to re-send on every connection from now on. Set it before the
   * socket is open and it is sent as soon as it is; set it after and it goes
   * straight away — so a caller never has to know which.
   */
  enter(msg: ClientMessage): void {
    this.hello = msg;
    this.send(msg);
  }

  /**
   * The message to re-send on the next connection, without sending it on this
   * one: for a better way back into a room this connection is already in — a
   * seat's token in place of the name that took the seat. Sending it now would
   * be answered with `joined`, and every `joined` would send it again.
   */
  rejoinWith(msg: ClientMessage): void {
    this.hello = msg;
  }

  /** Out of the room for good: a reconnection from now on goes nowhere until `enter`. */
  leave(): void {
    this.hello = null;
  }

  private open(): void {
    const ws = new WebSocket(SERVER_URL.replace(/^http/, 'ws'));
    this.ws = ws;
    this.emitStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    // Each handler first checks that `ws` is still the socket in use, because a
    // socket that has been closed goes on to say so, later. StrictMode connects,
    // closes and connects again in one go, so the first socket's close event
    // lands once the second is in place; taken for a drop, it would orphan the
    // second — still connected, still delivering — and open a third.
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.emitStatus('open');
      if (this.hello) ws.send(JSON.stringify(this.hello));
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      const msg = JSON.parse(String(ev.data)) as ServerMessage;
      for (const l of this.listeners) l(msg);
    };
    // A refused connection and a dropped one are the same thing to a player:
    // they are not connected, and something is trying.
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.retry();
    };
    ws.onerror = () => {
      if (this.ws !== ws) return;
      ws.close();
    };
  }

  private retry(): void {
    if (this.closed) return;
    this.ws = null;
    this.emitStatus('reconnecting');
    const wait = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
    this.attempt++;
    this.timer = setTimeout(() => this.open(), wait);
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: (link: Link) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private emitStatus(link: Link): void {
    for (const l of this.statusListeners) l(link);
  }

  close(): void {
    this.closed = true;
    this.hello = null;
    if (this.timer) clearTimeout(this.timer);
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      // Its close event is still to come, and nothing here is waiting for it.
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      ws.close();
    }
  }
}

export async function createRoom(
  playerCount: 2 | 3 | 4,
): Promise<{ roomCode: string; spectatorCode: string }> {
  const res = await fetch(`${SERVER_URL}/api/rooms`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ playerCount }),
  });
  if (!res.ok) throw new Error(`create room failed: ${res.status}`);
  return (await res.json()) as { roomCode: string; spectatorCode: string };
}
